import { Box } from '@material-ui/core';
import { formatMoney, formatPercent } from '@nemuru-eng/js-api-client';

import { getTidBySetupFeeType } from '@/components/shared/components/ConditionsSummary/LoanConditionsSummary/components/LoanConditions/constants/getTidBySetupFeeType';
import { LoanConditionsEntity } from '@/components/shared/components/ConditionsSummary/LoanConditionsSummary/components/LoanConditions/entities/LoanConditionsEntity';
import { CustomDivider } from '@/components/shared/components/CustomDivider';
import { Font } from '@/components/shared/components/Font';
import { InsuranceProviderLogo } from '@/components/shared/components/Logo/InsuranceProviderLogo/InsuranceProviderLogo';
import { ReadOnlyDataRow } from '@/components/shared/components/ReadOnlyDataRow';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';

const LoanConditionsData = ({
  loanConditions,
  productName,
}: {
  loanConditions: LoanConditionsEntity;
  productName: string;
}) => {
  const { getTextIdTranslation: t } = useTranslation();
  const displayInsurance = loanConditions.displayInsurance;

  return (
    <>
      <Box display={'flex'} flexDirection={'column'}>
        <ReadOnlyDataRow labelTextId={'PRODUCT'} value={<Text tId={productName} />} boxProps={{ py: 0 }} />
        {!!(loanConditions?.gracePeriod && loanConditions.gracePeriod > 0) && (
          <ReadOnlyDataRow
            labelTextId={'GRACE_PERIOD'}
            value={<Text tId={'X_MONTHS'} dynamicDataTexts={[loanConditions.gracePeriod.toString()]} />}
            boxProps={{ py: 0 }}
          />
        )}
        {loanConditions.term && (
          <ReadOnlyDataRow
            labelTextId={'TERM'}
            value={<Text tId={'X_MONTHS'} dynamicDataTexts={[loanConditions.term.toString()]} />}
            boxProps={{ py: 0 }}
          />
        )}
        {loanConditions.userDefinedQuota && (
          <ReadOnlyDataRow labelTextId={'MONTHLY_QUOTA'} value={loanConditions.userDefinedQuota} boxProps={{ py: 0 }} />
        )}
        <CustomDivider orientation={'horizontal'} margin={1} />
        {loanConditions.totalDownPayment && (
          <ReadOnlyDataRow labelTextId={'DOWN_PAYMENT'} value={loanConditions.totalDownPayment} />
        )}
        <ReadOnlyDataRow labelTextId={'PRINCIPAL_AMOUNT'} value={loanConditions.principal} boxProps={{ py: 0 }} />
        {loanConditions.pricingInsurance && loanConditions.insuranceCalculator ? (
          <Box py="2px" display="flex" justifyContent="space-between">
            <Box display="inline-flex" alignItems="center" gridGap={8}>
              <Font className="overflow-text-ellipsis" variant="subtitle2" color="textSecondary">
                {t(`INSURANCE_TYPE_${loanConditions.pricingInsurance.type}`)} (
                {formatPercent(loanConditions.pricingInsurance.cost.value)})
              </Font>
              <InsuranceProviderLogo size={16} insuranceProvider={loanConditions.pricingInsurance.provider} />
            </Box>
            <Font
              className="overflow-text-ellipsis"
              variant="h5"
              color="textPrimary"
              style={{ textAlign: 'right', paddingLeft: '8px' }}
            >
              {formatMoney(loanConditions.insuranceCalculator.insuranceTotalAmount())}
            </Font>
          </Box>
        ) : (
          displayInsurance && (
            <ReadOnlyDataRow
              labelTextId="INSURANCE"
              value={t(`INSURANCE_TYPE_${displayInsurance.type}`)}
              boxProps={{ py: 0 }}
            />
          )
        )}
      </Box>
      {!!(
        loanConditions.merchantDiscount &&
        loanConditions.merchantDiscount > 0 &&
        loanConditions.merchantDiscountPercentage
      ) && (
        <>
          <CustomDivider orientation={'horizontal'} margin={1} />
          <Box display={'flex'} flexDirection={'column'}>
            <ReadOnlyDataRow
              labelTextId={'DISCOUNT'}
              labelDynamicDataTexts={[loanConditions.merchantDiscountPercentage]}
              value={loanConditions.totalMerchantDiscount}
              boxProps={{ py: 0 }}
            />
            <ReadOnlyDataRow labelTextId={'MERCHANT_TOTAL_TO_COLLECT'} value={loanConditions.totalAmountToCollect} />
          </Box>
        </>
      )}

      <Box display={'flex'} flexDirection={'column'}>
        {loanConditions.setupFeeType && (
          <ReadOnlyDataRow
            labelTextId={getTidBySetupFeeType(loanConditions.setupFeeType, loanConditions.setupFee.relative.value)}
            labelDynamicDataTexts={[loanConditions.setupFee.relative.string]}
            value={loanConditions.setupFee.total.string}
            boxProps={{ py: 0 }}
          />
        )}
        {loanConditions.totalInterestPaid && (
          <ReadOnlyDataRow
            labelTextId={'INTERESTS_TO_PAY'}
            value={loanConditions.totalInterestPaid}
            boxProps={{ py: 0 }}
          />
        )}
        {loanConditions.totalAmountPaid && (
          <ReadOnlyDataRow
            labelTextId={'TOTAL_AMOUNT_TO_PAY'}
            value={loanConditions.totalAmountPaid}
            boxProps={{ py: 0 }}
          />
        )}
        <ReadOnlyDataRow
          labelTextId={'TIN'}
          value={formatPercent(loanConditions.annualInterest ?? 0)}
          boxProps={{ py: 0 }}
        />
        <ReadOnlyDataRow labelTextId={'TAE'} value={formatPercent(loanConditions.tae ?? 0)} boxProps={{ py: 0 }} />
      </Box>
    </>
  );
};

export { LoanConditionsData };
